import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Wand2 } from "lucide-react";
import { TRACK_OPTIONS } from "@/lib/school";
import { addDays, pyWeekday, monthOf, romeToday } from "@/lib/vigilanza";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { StudentPickerMulti } from "@/components/StudentPicker";

const chip = (on) => `px-3 py-1.5 rounded-full text-xs border transition-colors ${on ? "bg-[#1C352D] text-white border-[#1C352D]" : "bg-white border-stone-200 text-stone-600 hover:border-stone-400"}`;
const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

export function GeneratorSettings({ mansioni, loading, onGenerate }) {
  const today = romeToday().date;
  const [kind, setKind] = useState("week");
  const [weekDate, setWeekDate] = useState(addDays(today, 7 - pyWeekday(today)));
  const [month, setMonth] = useState(monthOf(today, 1).from.slice(0, 7));
  const [ids, setIds] = useState([]);
  const [years, setYears] = useState([]);
  const [tracks, setTracks] = useState([]);
  const [replace, setReplace] = useState(true);
  const [excluded, setExcluded] = useState([]);
  const { data: students = [] } = useQuery({
    queryKey: ["students-list", "attivo", "resid"],
    queryFn: async () => (await api.get("/students", { params: { status: "attivo", track_type: "residenziale" } })).data,
  });
  useEffect(() => { if (!ids.length && mansioni.length) setIds(mansioni.map((m) => m.id)); }, [mansioni]); // eslint-disable-line react-hooks/exhaustive-deps

  const range = kind === "week"
    ? { from: addDays(weekDate, -pyWeekday(weekDate)), to: addDays(weekDate, 6 - pyWeekday(weekDate)) }
    : monthOf(`${month}-01`, 0);

  return (
    <Card className="p-5 border border-stone-200 shadow-none rounded-xl space-y-5" data-testid="gen-settings">
      <div className="flex flex-wrap gap-6 items-end">
        <div>
          <Label className="text-xs">Calendario</Label>
          <div className="flex gap-1.5 mt-1.5">
            <button className={chip(kind === "week")} onClick={() => setKind("week")} data-testid="gen-kind-week">Settimanale</button>
            <button className={chip(kind === "month")} onClick={() => setKind("month")} data-testid="gen-kind-month">Mensile</button>
          </div>
        </div>
        {kind === "week" ? (
          <div><Label className="text-xs">Settimana che contiene il giorno</Label><Input type="date" value={weekDate} onChange={(e) => e.target.value && setWeekDate(e.target.value)} className="mt-1.5 w-44" data-testid="gen-week"/></div>
        ) : (
          <div><Label className="text-xs">Mese</Label><Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="mt-1.5 w-44" data-testid="gen-month"/></div>
        )}
        <div className="text-sm text-stone-600 pb-2" data-testid="gen-range">Dal <b>{range.from.split("-").reverse().join("/")}</b> al <b>{range.to.split("-").reverse().join("/")}</b></div>
      </div>

      <div>
        <div className="flex items-center gap-3"><Label className="text-xs">Mansioni da assegnare</Label>
          <button className="text-xs text-stone-500 underline" onClick={() => setIds(ids.length === mansioni.length ? [] : mansioni.map((m) => m.id))} data-testid="gen-toggle-all">{ids.length === mansioni.length ? "Nessuna" : "Tutte"}</button></div>
        <div className="flex gap-1.5 mt-1.5 flex-wrap">
          {mansioni.map((m) => (
            <button key={m.id} className={chip(ids.includes(m.id))} onClick={() => setIds(toggle(ids, m.id))} data-testid={`gen-mansione-${m.id}`}>
              <span className="inline-block w-2 h-2 rounded-full mr-1.5 align-middle" style={{ background: m.color }}/>{m.name} <span className="opacity-70">· {m.resources || 1}</span>
            </button>
          ))}
          {mansioni.length === 0 && <span className="text-sm text-stone-400">Nessuna mansione attiva nel catalogo.</span>}
        </div>
      </div>

      <div className="flex flex-wrap gap-6">
        <div><Label className="text-xs">Anno (vuoto = tutti)</Label>
          <div className="flex gap-1.5 mt-1.5">{[1, 2, 3].map((y) => <button key={y} className={chip(years.includes(y))} onClick={() => setYears(toggle(years, y))} data-testid={`gen-year-${y}`}>{y}° anno</button>)}</div></div>
        <div><Label className="text-xs">Percorso (vuoto = tutti)</Label>
          <div className="flex gap-1.5 mt-1.5">{TRACK_OPTIONS.map((t) => <button key={t} className={`${chip(tracks.includes(t))} capitalize`} onClick={() => setTracks(toggle(tracks, t))} data-testid={`gen-track-${t}`}>{t}</button>)}</div></div>
      </div>

      <div data-testid="gen-exclude-students">
        <Label className="text-xs">Escludi studenti per questa generazione {excluded.length > 0 && <span className="text-stone-500">({excluded.length})</span>}</Label>
        <div className="mt-1.5 max-w-xl">
          <StudentPickerMulti students={students} values={excluded} onChange={setExcluded} testId="gen-exclude-picker" placeholder="Cerca uno studente da escludere…"/>
        </div>
        <div className="text-[11px] text-stone-500 mt-1.5">I permessi di assenza approvati vengono già considerati in automatico nei giorni e negli orari indicati.</div>
      </div>

      <div className="flex flex-wrap items-center gap-4 justify-between">
        <label className="flex items-center gap-2 text-sm text-stone-600">
          <Checkbox checked={replace} onCheckedChange={(v) => setReplace(!!v)} data-testid="gen-replace"/> Sostituisci i turni già presenti per queste mansioni nel periodo
        </label>
        <Button onClick={() => onGenerate({ mansione_ids: ids, date_from: range.from, date_to: range.to, year_levels: years, track_types: tracks, excluded_student_ids: excluded, replace_existing: replace })}
          disabled={!ids.length || loading} className="rounded-full bg-[#E2725B] hover:bg-[#cc6550]" data-testid="gen-preview">
          <Wand2 size={14} className="mr-1.5"/> {loading ? "Calcolo…" : "Genera anteprima"}
        </Button>
      </div>
    </Card>
  );
}
